// Mercapoli - Mapa de Restaurantes Cercanos con Leaflet
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Clock, DollarSign, Star, Loader2, RefreshCw, LocateFixed } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { maps as mapsApi } from '../lib/api';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom icons
const restaurantIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const userIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map
const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, 14);
    }
  }, [position, map]);
  return null;
};

const RestaurantMap = ({ onSelectRestaurant, height = "400px" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [restaurantes, setRestaurantes] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [radioKm, setRadioKm] = useState(5);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Default location: Guatemala City
  const defaultLocation = { lat: 14.6349, lng: -90.5069 };

  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (userLocation) {
      loadRestaurantes();
    }
  }, [userLocation, radioKm]);

  const getUserLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setUserLocation(defaultLocation);
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation(defaultLocation);
      setGettingLocation(false);
    }
  };

  const loadRestaurantes = async () => {
    if (!userLocation) return;
    
    setLoading(true);
    try {
      const data = await mapsApi.getRestaurantesCercanos(
        userLocation.lat,
        userLocation.lng,
        radioKm
      );
      setRestaurantes(data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    }
    setLoading(false);
  };

  const handleSelectRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    if (onSelectRestaurant) {
      onSelectRestaurant(restaurant);
    }
  };

  const goToRestaurant = (restauranteId) => {
    navigate(`/app/restaurante/${restauranteId}`);
  };

  const mapCenter = userLocation || defaultLocation;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={getUserLocation}
            disabled={gettingLocation}
          >
            {gettingLocation ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <LocateFixed className="h-4 w-4 mr-2" />
            )}
            Mi ubicación
          </Button>
          <Button variant="outline" size="sm" onClick={loadRestaurantes} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Radio:</span>
          {[3, 5, 10].map((km) => (
            <Button
              key={km}
              variant={radioKm === km ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRadioKm(km)}
              className={radioKm === km ? 'bg-brand-primary' : ''}
            >
              {km} km
            </Button>
          ))}
        </div>
      </div>

      {/* Map Container */}
      <div className="rounded-xl overflow-hidden border border-border" style={{ height }}>
        {userLocation ? (
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <RecenterMap position={[mapCenter.lat, mapCenter.lng]} />
            
            {/* User location marker */}
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Tu ubicación</strong>
                </div>
              </Popup>
            </Marker>

            {/* Radius circle */}
            <Circle
              center={[userLocation.lat, userLocation.lng]}
              radius={radioKm * 1000}
              pathOptions={{
                color: '#00BFA6',
                fillColor: '#00BFA6',
                fillOpacity: 0.1,
                weight: 2
              }}
            />

            {/* Restaurant markers */}
            {restaurantes.map((rest) => (
              <Marker
                key={rest.restaurante_id}
                position={[rest.latitud || defaultLocation.lat, rest.longitud || defaultLocation.lng]}
                icon={restaurantIcon}
                eventHandlers={{
                  click: () => handleSelectRestaurant(rest)
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-bold text-lg mb-1">{rest.nombre}</h3>
                    <div className="space-y-1 text-sm">
                      <p className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {rest.distancia_km} km
                      </p>
                      <p className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Envío: Q{rest.tarifa_entrega}
                      </p>
                      <p className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        ~{rest.tiempo_estimado_min} min
                      </p>
                      <p className="flex items-center gap-1">
                        <Star className="h-3 w-3 text-yellow-500" />
                        {rest.calificacion || 4.5}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-2 bg-brand-primary"
                      onClick={() => goToRestaurant(rest.restaurante_id)}
                    >
                      Ver menú
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-muted">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-brand-primary" />
              <p className="text-muted-foreground">Obteniendo ubicación...</p>
            </div>
          </div>
        )}
      </div>

      {/* Restaurant list below map */}
      {!loading && restaurantes.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading font-bold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-brand-primary" />
            {restaurantes.length} restaurantes cercanos
          </h3>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {restaurantes.slice(0, 6).map((rest) => (
              <motion.div
                key={rest.restaurante_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
              >
                <Card
                  className={`cursor-pointer transition-all ${
                    selectedRestaurant?.restaurante_id === rest.restaurante_id
                      ? 'ring-2 ring-brand-primary'
                      : ''
                  }`}
                  onClick={() => goToRestaurant(rest.restaurante_id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                        {rest.imagen ? (
                          <img src={rest.imagen} alt={rest.nombre} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <MapPin className="h-6 w-6 text-brand-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold truncate">{rest.nombre}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{rest.distancia_km} km</span>
                          <span>•</span>
                          <span>~{rest.tiempo_estimado_min} min</span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <Badge variant="secondary" className="text-xs">
                            Envío: Q{rest.tarifa_entrega}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                            <span>{rest.calificacion || 4.5}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {!loading && restaurantes.length === 0 && userLocation && (
        <Card>
          <CardContent className="p-8 text-center">
            <MapPin className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No hay restaurantes en un radio de {radioKm} km</p>
            <p className="text-sm text-muted-foreground">Intenta aumentar el radio de búsqueda</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RestaurantMap;
